import { FEATURE_SOURCES, coerceValue, pickFirstNonNullValueForFlagType } from '~coreServer/features';
import { cloneDeep } from 'lodash-es';

/**
 * Creates and returns a Feature Flag Manager instance
 *
 * @param {Object} options - Configuration options for the feature flag manager
 * @param {Object} options.initialFeatureValues - Initial feature flag values with their sources and values
 * @param {Object<string, Object>} options.featureConfig - Feature flag configuration with definitions and metadata
 * @param {Object|null} options.splitClient - Split.io client instance for retrieving split-based flags
 * @param {Object} options.splitData - Data related to Split.io integration
 * @param {Object} options.splitData.attributes - Key-value pairs of attributes for Split.io targeting
 * @param {Object} options.splitData.identity - Identity object for server-side Split.io evaluation
 * @param {Array} options.splitData.ssrImpressions - List of flag IDs that were touched during SSR
 * @param {boolean} options.isServer - Whether the code is running on the server
 * @param {Object} options.logger - Logger instance for debugging and error reporting
 * @returns {Object} Feature flag manager instance
 */
export default function createFeatureFlagManager({
    initialFeatureValues,
    featureConfig,
    splitClient,
    splitData = {
        attributes: {},
        identity: null,
        ssrImpressions: [],
    },
    isServer,
    logger,
}) {
    const featureValues = { ...initialFeatureValues };
    const { attributes: initialSplitAttributes, identity: splitIdentity, ssrImpressions } = splitData || {};
    const splitAttributes = { ...initialSplitAttributes };
    const ssrSplitTouched = new Set(ssrImpressions);

    /**
     * Gets the value of a specified feature flag.
     *
     * @param {string} flagId - The identifier of the feature flag
     * @returns {string | number | boolean} The value of the feature flag
     * @throws {Error} If the flag is not found
     */
    function getFlagValue(flagId) {
        const featureFlag = featureValues[flagId];
        const flagConfig = featureConfig[flagId];

        if (!featureFlag || !flagConfig) {
            throw new Error(`Feature flag "${flagId}" not found.`);
        }

        // if value is not set with split, just return the value
        if (featureFlag.source !== FEATURE_SOURCES.split) {
            return featureFlag.value;
        }

        // eslint-disable-next-line no-use-before-define
        return getSplitFlagValue(flagId);
    }

    /**
     * Gets the value of a split-source feature flag. Should never be called directly, internal use only.
     *
     * @param {string} flagId - The identifier of the split feature flag
     * @returns {string | number | boolean} The value of the feature flag
     * @throws {Error} If split client does not exist
     * @private
     */
    function getSplitFlagValue(flagId) {
        if (!splitClient) {
            throw new Error(`Split client is required to fetch flags from split  -  "${flagId}".`);
        }

        const featureFlag = featureValues[flagId];
        const flagConfig = featureConfig[flagId];
        const { splitInfo } = flagConfig;
        const { splitName } = splitInfo;
        let res;

        const cleanAttributes = Object.fromEntries(Object.entries(splitAttributes).filter(([, v]) => v != null));

        if (isServer) {
            // save server fetched split flags to record impressions in the client
            ssrSplitTouched.add(flagId);

            if (!splitIdentity) {
                logger.error(`User has no splitIdentity, cannot fetch flag "${flagId}"`);
            }

            res = splitClient.getTreatment(splitIdentity?.key, splitName, cleanAttributes);
        } else {
            res = splitClient.getTreatment(splitName, cleanAttributes);
        }

        const newSplitValue = coerceValue(res, flagConfig.multivariate);
        featureFlag.value = newSplitValue;
        featureFlag.values.split = newSplitValue;

        if (newSplitValue === null) {
            const { source, value } = pickFirstNonNullValueForFlagType(featureFlag.values, flagConfig.multivariate);
            featureFlag.value = value;
            featureFlag.source = source;
        }

        return featureFlag.value;
    }

    /**
     * Updates the attributes used for Split.io targeting.
     *
     * @param {Object} attrs - Key-value pairs of attributes to merge with existing attributes
     */
    function updateSplitAttributes(attrs) {
        Object.assign(splitAttributes, attrs);
    }

    /**
     * Gets all feature flag values in a simplified format.
     *
     * @returns {Object} Key-value pairs of flag IDs and their current values
     */
    function getAllFlagValues() {
        return Object.keys(featureValues)
            .reduce((acc, k) => Object.assign(acc, {
                [k]: featureValues[k].value,
            }), {});
    }

    /**
     * Gets detailed information about all feature flags including sources and values.
     *
     * @returns {Object} Detailed feature flag information
     */
    function getAllDetailedFlagValues() {
        return cloneDeep(featureValues);
    }

    /**
     * Replays split impressions that were collected during server-side rendering.
     */
    function replaySsrSplitImpressions() {
        if (splitClient) {
            const ssrFlagIds = Array.from(ssrSplitTouched);
            logger.info('Replaying SSR split impressions for flags: ', ssrFlagIds);
            ssrFlagIds.forEach(id => getSplitFlagValue(id));
        }
    }

    /**
     * Gets data needed for client-side hydration during server-side rendering.
     *
     * @returns {Object} Object containing feature values and split data for hydration
     * @returns {Object} featureValues - All detailed feature flag values
     * @returns {Object} splitData - Split.io related data for hydration
     * @returns {Object} splitData.identity - Split identity for server-side evaluation
     * @returns {Object} splitData.attributes - Split attributes for targeting
     * @returns {Array} splitData.ssrImpressions - List of split flags accessed during SSR
     */
    function getSSRHydrationData() {
        return {
            featureValues: cloneDeep(featureValues),
            splitData: {
                identity: cloneDeep(splitIdentity || null),
                attributes: cloneDeep(splitAttributes || {}),
                ssrImpressions: Array.from(ssrSplitTouched || []),
            },
        };
    }

    /**
     * Creates a proxy object for feature flags access - this is how we should access flags throughout the application
     * @returns {Object} Proxy object for feature flags
     */
    function createFeaturesProxy() {
        const proxy = new Proxy({}, {
            get(target, prop) {
                if (typeof prop !== 'string') return undefined;

                if (!featureValues[prop] || !featureConfig[prop]) {
                    logger.error(`Feature flag "${prop}" not found.`);
                    return undefined;
                }

                return getFlagValue(prop);
            },
            set() {
                throw new Error('Cannot mutate feature flags via proxy');
            },
            defineProperty() {
                throw new Error('Cannot define new feature flag via proxy');
            },
            deleteProperty() {
                throw new Error('Cannot delete feature flags via proxy');
            },
        });

        return Object.freeze(proxy);
    }

    return {
        features: createFeaturesProxy(),
        getFlagValue,
        updateSplitAttributes,
        getAllFlagValues,
        getAllDetailedFlagValues,
        replaySsrSplitImpressions,
        getSSRHydrationData,
    };
}
